import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, parseJSON } from "date-fns";
import { Box, Typography, useTheme } from "@mui/material";
import ChatIcon from "@icons/myicons/chat.svg?react";
import CancelledIcon from "@icons/myicons/cancelled.svg?react";
import {
  getNumberOfLotsByInvestmentAndBookingType,
  getValueByInvestmentAndBookingType,
  toCurrency,
  toPercent,
} from "@helpers";
import { BookingType } from "@components";
import { TableConfig, Tooltip } from "@rentablo/components";
import { TransactionStatusIcon } from "./TransactionStatusIcon";
import { BookingsTableMoreAction } from "./BookingsTableMoreAction";
import {
  TableBookingData,
  TypographyColored,
  ValueDateWrapper,
} from "./BookingsSingleInvestmentTable";

type RendererProps = {
  row: TableBookingData;
};

const TypeAndDateCell = ({ row }: RendererProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const iconStyle = {
    width: "16px",
    height: "16px",
    color: theme.palette.text.secondary,
    cursor: "help",
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap={2}>
        <BookingType type={row.bookingType} />
        {row.isCancelled && (
          <Tooltip text={t("investment.protocolTable.cancelled")}>
            <Box display="flex">
              <CancelledIcon style={iconStyle} />
            </Box>
          </Tooltip>
        )}
        {row.comment && (
          <Tooltip
            title={t("investment.protocolTable.comment")}
            text={row.comment}
          >
            <Box display="flex">
              <ChatIcon style={iconStyle} />
            </Box>
          </Tooltip>
        )}
      </Box>
      <ValueDateWrapper>
        <TransactionStatusIcon
          dataConsistencyStatus={row.dataConsistencyStatus}
          isExternalID={row.isExternalID}
        />
        <Typography variant="body2" ml={2} color="textSecondary">
          {format(parseJSON(row.valueDate), "dd.MM.yyyy")}
        </Typography>
      </ValueDateWrapper>
    </>
  );
};

const AmountCell = ({ row }: RendererProps) => {
  const { t } = useTranslation();
  const numberOfLotsText = getNumberOfLotsByInvestmentAndBookingType({
    value: row.numberOfLots,
    investmentType: row.investmentType,
    bookingType: row.bookingType,
  });
  const valueText = getValueByInvestmentAndBookingType({
    value: row.securityPrice,
    investmentType: row.investmentType,
    bookingType: row.bookingType,
    percentageRange: "0-100",
    currency: row.currency,
    quoteNotation: row.quoteNotation,
  });
  const amountText = numberOfLotsText
    ? numberOfLotsText + " " + valueText
    : valueText;

  return (
    <>
      <Typography variant="body1">{amountText}</Typography>
      {row.priceChange ? (
        <div>
          <Typography variant="body2" color="textPrimary" display="inline">
            {t("investment.protocolTable.gainersAndLosersLabel")}:{" "}
          </Typography>
          <TypographyColored
            variant="body2"
            lots={row.priceChange}
            display="inline"
          >
            {toPercent(row.priceChange)}
          </TypographyColored>
        </div>
      ) : null}
    </>
  );
};

const ExpensesCell = ({ row }: RendererProps) => (
  <Typography variant="body2">
    {toCurrency(row.expenses, row.currency)}
  </Typography>
);

const TotalSumCell = ({ row }: RendererProps) => (
  <>
    <Typography variant="body2">
      {toCurrency(row.amount, row.currency)}
    </Typography>
    <Typography variant="body1">
      {row.bookingType === "sell" && row.grossGain
        ? toCurrency(row.grossGain, row.currency)
        : null}
    </Typography>
  </>
);

const ActionsCell = ({ row }: RendererProps) => (
  <BookingsTableMoreAction
    bookingId={row.id}
    investmentId={row.investmentId}
    bookingType={row.bookingType}
  />
);

type SortArgs = {
  sortField?: string;
  sortDirection?: TableSortDirection;
  onSort: (field: string) => void;
};

export const useInvestmentBookingsTableConfig = ({
  sortField,
  sortDirection,
  onSort,
}: SortArgs): TableConfig<TableBookingData> => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      id: "investment-bookings-table",
      getRowId: (row) => row.id,
      sorting:
        sortField && sortDirection
          ? { field: sortField, direction: sortDirection }
          : undefined,
      onSort,
      columns: [
        {
          id: "valueDate",
          sortKey: "valueDate",
          title: t("investment.protocolTable.bookingType"),
          renderer: TypeAndDateCell,
          sortable: true,
        },
        {
          id: "price",
          sortKey: "securityPrice",
          title: t("investment.protocolTable.amount"),
          renderer: AmountCell,
          sortable: true,
        },
        {
          id: "expenses",
          title: t("investment.protocolTable.expenses"),
          renderer: ExpensesCell,
        },
        {
          id: "totalSum",
          sortKey: "amount",
          title: t("investment.protocolTable.totalSum"),
          renderer: TotalSumCell,
          sortable: true,
        },
        {
          id: "actions",
          title: t("investment.protocolTable.actions.title"),
          renderer: ActionsCell,
          align: "right",
        },
      ],
    }),
    [t, sortField, sortDirection, onSort]
  );
};
